import { useEffect } from "react";
import {
	BrowserRouter as Router,
	Routes,
	Route,
	Navigate,
} from "react-router-dom";
import Layout from "./components/layout/Layout";
import Home from "./pages/Home";
import Login from "./pages/Login/Login";
import { useAuthStore } from "./store/authStore";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./styles/globals.css";
import "./styles/variables.css";

// 티켓 관리 페이지 임포트
import TicketRegister from "./pages/Ticket/Register";
import TicketStatus from "./pages/Ticket/Status";
import TicketDaily from "./pages/Ticket/Daily";

// 행사 정리 페이지 임포트
import EventSeat from "./pages/Event/Seat";
import EventSMS from "./pages/Event/SMS";
import EventReservation from "./pages/Event/Reservation";

// 기타 관리 페이지 임포트
import ManagementSchedule from "./pages/Management/Schedule";
import ManagementCall from "./pages/Management/Call";
import ManagementLog from "./pages/Management/Log";
import SeatmapTrainTypes from "./pages/Management/SeatmapTrainTypes";
import SeatmapEditor from "./pages/Management/SeatmapEditor";

// 버스 좌석 배정 페이지 임포트
import BusInfo from "./pages/Bus/Info";
import BusSeat from "./pages/Bus/Seat";
import BusConfirmed from "./pages/Bus/Confirmed";

// 기타 편의기능 페이지 임포트
import UtilityTrainNumber from "./pages/Utility/TrainNumber";
import UtilityTrainInfo from "./pages/Utility/TrainInfo";

// 기존 좌석배치 시스템 페이지 임포트
import LegacyTrainSeat from "./pages/Legacy/TrainSeat";
import LegacySeatRegister from "./pages/Legacy/SeatRegister";
import LegacyTrainType from "./pages/Legacy/TrainType";
import LegacyStation from "./pages/Legacy/Station";
import LegacyReservation from "./pages/Legacy/Reservation";
import LegacyTrain from "./pages/Legacy/Train";

// 관리자 페이지 임포트
import Admin from "./pages/Admin";

// 인증이 필요한 라우트를 위한 컴포넌트
const ProtectedRoute = ({ children }) => {
	const { isAuthenticated } = useAuthStore();

	if (!isAuthenticated) {
		return <Navigate to="/login" replace />;
	}

	return children;
};

// 이미 인증된 사용자를 위한 라우트 (로그인 페이지 접근 방지)
const PublicRoute = ({ children }) => {
	const { isAuthenticated } = useAuthStore();

	if (isAuthenticated) {
		return <Navigate to="/" replace />;
	}

	return children;
};

function App() {
	const { restoreUser } = useAuthStore();

	useEffect(() => {
		// 앱 시작시 저장된 사용자 정보 복원
		restoreUser();
	}, [restoreUser]);

	return (
		<Router>
			<ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
			<Routes>
				{/* 공개 라우트 */}
				<Route
					path="/login"
					element={
						<PublicRoute>
							<Login />
						</PublicRoute>
					}
				/>

				{/* 보호된 라우트 */}
				<Route
					path="/"
					element={
						<ProtectedRoute>
							<Layout>
								<Home />
							</Layout>
						</ProtectedRoute>
					}
				/>

				{/* 티켓 관리 라우트 */}
				<Route
					path="/ticket/register"
					element={
						<ProtectedRoute>
							<Layout>
								<TicketRegister />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/ticket/status"
					element={
						<ProtectedRoute>
							<Layout>
								<TicketStatus />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/ticket/daily"
					element={
						<ProtectedRoute>
							<Layout>
								<TicketDaily />
							</Layout>
						</ProtectedRoute>
					}
				/>

				{/* 행사 정리 라우트 */}
				<Route
					path="/event/seat"
					element={
						<ProtectedRoute>
							<Layout>
								<EventSeat />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/event/sms"
					element={
						<ProtectedRoute>
							<Layout>
								<EventSMS />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/event/reservation"
					element={
						<ProtectedRoute>
							<Layout>
								<EventReservation />
							</Layout>
						</ProtectedRoute>
					}
				/>

				{/* 기타 관리 라우트 */}
				<Route
					path="/management/schedule"
					element={
						<ProtectedRoute>
							<Layout>
								<ManagementSchedule />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/management/call"
					element={
						<ProtectedRoute>
							<Layout>
								<ManagementCall />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/management/log"
					element={
						<ProtectedRoute>
							<Layout>
								<ManagementLog />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/management/seatmap-train-types"
					element={
						<ProtectedRoute>
							<Layout>
								<SeatmapTrainTypes />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/management/seatmap-editor"
					element={
						<ProtectedRoute>
							<Layout>
								<SeatmapEditor />
							</Layout>
						</ProtectedRoute>
					}
				/>

				{/* 버스 좌석 배정 라우트 */}
				<Route
					path="/bus/info"
					element={
						<ProtectedRoute>
							<Layout>
								<BusInfo />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/bus/seat"
					element={
						<ProtectedRoute>
							<Layout>
								<BusSeat />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/bus/confirmed"
					element={
						<ProtectedRoute>
							<Layout>
								<BusConfirmed />
							</Layout>
						</ProtectedRoute>
					}
				/>

				{/* 기타 편의기능 라우트 */}
				<Route
					path="/utility/train-number"
					element={
						<ProtectedRoute>
							<Layout>
								<UtilityTrainNumber />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/utility/train-info"
					element={
						<ProtectedRoute>
							<Layout>
								<UtilityTrainInfo />
							</Layout>
						</ProtectedRoute>
					}
				/>

				{/* 기존 좌석배치 시스템 라우트 */}
				<Route
					path="/legacy/train-seat"
					element={
						<ProtectedRoute>
							<Layout>
								<LegacyTrainSeat />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/legacy/seat-register"
					element={
						<ProtectedRoute>
							<Layout>
								<LegacySeatRegister />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/legacy/train-type"
					element={
						<ProtectedRoute>
							<Layout>
								<LegacyTrainType />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/legacy/station"
					element={
						<ProtectedRoute>
							<Layout>
								<LegacyStation />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/legacy/reservation"
					element={
						<ProtectedRoute>
							<Layout>
								<LegacyReservation />
							</Layout>
						</ProtectedRoute>
					}
				/>
				<Route
					path="/legacy/train"
					element={
						<ProtectedRoute>
							<Layout>
								<LegacyTrain />
							</Layout>
						</ProtectedRoute>
					}
				/>

				{/* 관리자 페이지 라우트 */}
				<Route
					path="/admin"
					element={
						<ProtectedRoute>
							<Layout>
								<Admin />
							</Layout>
						</ProtectedRoute>
					}
				/>

				{/* 대시보드 라우트 */}
				<Route
					path="/dashboard"
					element={
						<ProtectedRoute>
							<Layout>
								<div className="p-6">
									<h1 className="text-2xl font-bold mb-4">
										대시보드
									</h1>
									<p>
										홈익여행사 TMS 시스템에 오신 것을 환영합니다.
									</p>
								</div>
							</Layout>
						</ProtectedRoute>
					}
				/>

				{/* 존재하지 않는 경로는 홈으로 리다이렉트 */}
				<Route path="*" element={<Navigate to="/" replace />} />
			</Routes>
		</Router>
	);
}

export default App;
